import type { ReactNode } from 'react';
import { testId } from '../../app/testId';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './Toast.module.css';

// The toast itself — DESIGN_SYSTEM §12 row 25 (`.toast`): inverse surface, an icon, the message (bold lead),
// an optional action ("Undo", "Try again"), a dismiss button, and the countdown bar. Presentational: the
// stack (ToastProvider) owns timers, pausing and the announcement. Not a live region — the app has ONE
// (`app/live/LiveRegion`), and the provider speaks through it.

export type ToastTone = 'success' | 'info' | 'error';

export const TOAST_ICON: Record<ToastTone, IconName> = {
  success: 'check-circle',
  info: 'info',
  error: 'alert-circle',
};

export interface ToastAction {
  label: string;
  onClick: () => void;
  /** Test id of the action button (`toast.undo`, TEST_PLAN §1.4). */
  testID?: string;
}

export interface ToastCardProps {
  message: ReactNode;
  tone?: ToastTone;
  icon?: IconName;
  action?: ToastAction;
  onDismiss?: () => void;
  /** Countdown in ms; 0 or undefined draws no bar. */
  duration?: number;
  /** Freezes the countdown bar (the provider pauses on hover / focus). */
  paused?: boolean;
  /** Draws no countdown bar (reduced motion). */
  showTimer?: boolean;
  dismissLabel?: string;
  /** Test id of the card; the dismiss button is `<testID>.dismiss`. */
  testID?: string;
  className?: string;
}

export function ToastCard({
  message,
  tone = 'info',
  icon,
  action,
  onDismiss,
  duration,
  paused = false,
  showTimer = true,
  dismissLabel = 'Dismiss',
  testID,
  className,
}: ToastCardProps) {
  const timed = showTimer && duration !== undefined && duration > 0;
  return (
    <div
      className={cx(styles.card, tone === 'error' && styles.error, className)}
      data-tone={tone}
      data-paused={paused || undefined}
      data-testid={testID}
    >
      <Icon name={icon ?? TOAST_ICON[tone]} className={styles.icon} />
      <span className={styles.message}>{message}</span>
      {action ? (
        <button
          type="button"
          className={styles.action}
          data-testid={action.testID}
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : null}
      {onDismiss ? (
        <button
          type="button"
          className={styles.close}
          aria-label={dismissLabel}
          data-testid={testID ? testId(testID, 'dismiss') : undefined}
          onClick={onDismiss}
        >
          <Icon name="x" size={14} />
        </button>
      ) : null}
      {timed ? (
        <span
          className={styles.timer}
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}
